import { Grid, GridProps, GridSize } from '@mui/material'
import clsx from 'clsx'
import { useStyles } from './DetailRow.styles'
import { LabraToolTipV2, VerticalDivider } from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons'
import { ShortenedNameV2 } from '@labrav/common'

type DetailInfoProps = {
  title?: string | JSX.Element
  value?: string | JSX.Element
  keyName?: string
  className?: string
  infoTooltip?: string
  gridSize?: GridSize
  varient?: 'small' | 'medium'
  align?: string
  shortenedValue?: boolean
  titleClassName?: string
  valueClassName?: string
}

type BuyerInfoProps = {
  title?: string | JSX.Element
  value?: string | JSX.Element
  keyName?: string
  className?: string
  showDivider?: boolean
  gridSize?: GridSize
  /** Full string shown in tooltip when value is ellipsized in the cell */
  valueTooltip?: string
  /** Removes right padding/gap before the column divider */
  compactRight?: boolean
}

export const DetailInfo = ({
  value,
  title,
  keyName,
  className,
  infoTooltip,
  gridSize = 6 as GridSize,
  align,
  shortenedValue = false,
  titleClassName,
  valueClassName,
}: DetailInfoProps) => {
  const { classes } = useStyles({ align })
  const classesWrapper = clsx(
    classes.detailSectionWrapper,
    className ? className : '',
    align ? classes.align : ''
  )
  return (
    <Grid
      className={classesWrapper}
      size={{
        sm: gridSize,
        lg: gridSize,
        md: gridSize,
      }}
    >
      {title && (
        <div
          className={clsx(classes.title, titleClassName ? titleClassName : '')}
          data-testid={`${keyName}-key`}
        >
          {title}

          {infoTooltip && (
            <div className={classes.infoIcon}>
              <LabraToolTipV2
                tooltipPlacement={'bottom-start'}
                description={infoTooltip}
                tooltipPlacmentGap="12px"
              >
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  style={{
                    height: 16,
                    color: '#6A777A',
                    width: 16,
                    display: 'flex',
                  }}
                />
              </LabraToolTipV2>
            </div>
          )}
        </div>
      )}

      {value &&
        (shortenedValue ? (
          <div className={classes.value}>
            <ShortenedNameV2 name={value} length={30} />
          </div>
        ) : (
          <div
            className={clsx(
              classes.value,
              valueClassName ? valueClassName : ''
            )}
            data-testid={`${keyName}-value`}
          >
            {value}
          </div>
        ))}
    </Grid>
  )
}

export const BuyerInfo = ({
  value,
  title,
  keyName,
  className,
  showDivider = true,
  gridSize = 3,
  valueTooltip,
  compactRight = false,
}: BuyerInfoProps) => {
  const { classes } = useStyles({})
  const valueNode =
    value &&
    (valueTooltip ? (
      <LabraToolTipV2 description={valueTooltip} tooltipPlacement="top">
        <div
          className={clsx(classes.value, classes.valueTooltipTrigger)}
          data-testid={`${keyName}-value`}
        >
          {value}
        </div>
      </LabraToolTipV2>
    ) : (
      <div className={classes.value} data-testid={`${keyName}-value`}>
        {value}
      </div>
    ))

  return (
    <Grid
      className={clsx(classes.detailSectionWrapper, className ? className : '')}
      size={{
        sm: gridSize,
        lg: gridSize,
        md: gridSize,
      }}
    >
      <div
        className={clsx(
          classes.detailsContainer,
          compactRight && classes.detailsContainerCompactRight
        )}
      >
        <div className={classes.detailsContent}>
          {title && (
            <div className={classes.title} data-testid={`${keyName}-key`}>
              {title}
            </div>
          )}
          {valueNode}
        </div>
        {showDivider && (
          <div className={classes.divider}>
            {' '}
            <VerticalDivider height="44px"></VerticalDivider>{' '}
          </div>
        )}
      </div>
    </Grid>
  )
}

export const DetailRow = ({
  children,
  borderBottom = false,
  className,
  props,
}: {
  children: JSX.Element
  borderBottom?: boolean
  className?: string
  props?: GridProps
}) => {
  const { classes } = useStyles({})
  return (
    <Grid
      container
      className={clsx(
        classes.detailRow,
        borderBottom ? classes.borderBottom : '',
        className ? className : ''
      )}
      data-testid={'detailed-row'}
      {...props}
    >
      {children}
    </Grid>
  )
}
export const BuyerDetailRow = ({
  children,
  className,
}: {
  children: JSX.Element
  className?: string
}) => {
  const { classes } = useStyles({})
  return (
    <Grid
      className={clsx(classes.buyerDetailRow, className ? className : '')}
      data-testid={'buyer-detailed-row'}
      container
    >
      {children}
    </Grid>
  )
}
